import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import type { CheckInPhoto } from '@techgitter/contracts';
import '../sessionDetail/dialogs/checkInDialog.css';

export interface DeletePhotoDialogProps {
  /** The photo about to go, or null when the dialog is closed. */
  photo: CheckInPhoto | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * Destructive confirmation for removing an inspection photo (sm4rt-web-ui
 * skill §6). Same on-system shell as `RemoveDeviceDialog`, with the photo
 * itself shown so the technician confirms the one they meant — thumbnails at
 * 80px are easy to mis-tap.
 *
 * The API soft-deletes the row and keeps the blob, but nothing in the app can
 * bring a removed photo back, so it reads as permanent here.
 */
export const DeletePhotoDialog: React.FC<DeletePhotoDialogProps> = ({ photo, onClose, onConfirm }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = photo !== null;

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  if (!photo) return null;

  const src = photo.signedUrl || photo.blobUrl;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete the photo');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) handleClose();
  };

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog" role="dialog" aria-modal="true" aria-label="Delete photo">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Delete photo</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body">
          {src && (
            <img
              src={src}
              alt={photo.originalFilename ?? 'Inspection photo'}
              style={{
                display: 'block',
                width: '100%',
                maxHeight: 220,
                objectFit: 'contain',
                background: '#f0f3f7',
                borderRadius: 8,
              }}
            />
          )}
          <div className="ci-dialog__desc">
            This photo will be removed from the device's inspection record.
          </div>

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button
            type="button"
            className="ci-dialog__cancel"
            onClick={handleClose}
            disabled={submitting}
          >
            Keep photo
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--danger"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Deleting…' : 'Delete photo'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
